// src/components/MonthlyFeatured/MonthlyFeatured.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AOS from "aos";
import "aos/dist/aos.css";
import { useTheme } from "../../context/ThemeContext";
import ArtistCard from "../../components/Esiintyjakortti/Esiintyjakortti";
import { api } from "../../services/api";

/**
 * Valitsee kuukauden esiintyjän listasta.
 * Näytetään vain esiintyjiä, joilla on palautteita.
 */
function pickMonthlyFeatured(bands = []) {
  if (!Array.isArray(bands) || bands.length === 0) return null;

  // Näytä vain jos vähintään yhdellä on palautteita
  const hasReviews = (b) => Number(b?.total_reviews ?? b?.reviews_count ?? 0) > 0;
  if (!bands.some(hasReviews)) return null;

  const now = new Date();
  const monthStart = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);
  const monthEnd   = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);

  // Rajaa vain palautteita saaneet
  const reviewed = bands.filter(hasReviews);

  // Valinta: ensisijaisesti tämä kuukausi, sitten flagi, sitten paras arvosana/arviomäärä
  const inThisMonth = (b) => {
    if (!b?.featured_until) return false;
    const t = new Date(String(b.featured_until).replace(" ", "T"));
    return t >= monthStart && t <= monthEnd;
  };

  const explicitThisMonth = reviewed.find(inThisMonth);
  if (explicitThisMonth) return explicitThisMonth;

  const explicitFlag = reviewed.find((b) => Number(b.is_monthly_featured) === 1);
  if (explicitFlag) return explicitFlag;

  const withMinReviews = reviewed
    .filter((b) => Number(b.total_reviews ?? b.reviews_count ?? 0) >= 3)
    .sort((a, b) => {
      const aAvg = Number(a.average_rating ?? 0);
      const bAvg = Number(b.average_rating ?? 0);
      const byAvg = bAvg - aAvg;
      if (byAvg !== 0) return byAvg;
      const aTot = Number(a.total_reviews ?? a.reviews_count ?? 0);
      const bTot = Number(b.total_reviews ?? b.reviews_count ?? 0);
      return bTot - aTot;
    });
  if (withMinReviews[0]) return withMinReviews[0];

  const any = [...reviewed].sort(
    (a, b) => Number(b.average_rating ?? 0) - Number(a.average_rating ?? 0)
  );
  return any[0] || null;
}

export default function MonthlyFeatured() {
  const { theme } = useTheme();
  const navigate = useNavigate();

  const [bands, setBands] = useState([]);
  const [loading, setLoading] = useState(true);

  const cardBg   = theme === "dark" ? "bg-[#0b0b0f]/60 ring-white/10" : "bg-lemon/60 ring-white/40";
  const bodyText = theme === "dark" ? "text-white/85" : "text-black/80";
  const titleText= theme === "dark" ? "text-white" : "text-black/80";

  useEffect(() => {
    AOS.init({ duration: 900, once: true });
  }, []);

  // Hae bändit proxy-APIn kautta
  useEffect(() => {
    const ac = new AbortController();
    (async () => {
      try {
        // Jos haluat rajata päivän mukaan, ota localStoragesta päivä ja käytä api.freeBands(date, { signal })
        const list = await api.bands({ signal: ac.signal });
        setBands(Array.isArray(list) ? list : (list?.bands ?? []));
      } catch (e) {
        if (e.name !== "AbortError") console.error("Bändien haku epäonnistui:", e);
      } finally {
        setLoading(false);
      }
    })();
    return () => ac.abort();
  }, []);

  const monthlyFeatured = useMemo(() => pickMonthlyFeatured(bands), [bands]);

  if (loading) {
    return (
      <section className={`mt-8 rounded-2xl ${cardBg} backdrop-blur-md shadow-lg ring-1 p-6`} data-aos="fade-up">
        <p className={`${bodyText} text-center`}>Ladataan kuukauden esiintyjää…</p>
      </section>
    );
  }

  // Ei näytetä mitään, jos kenelläkään ei ole palautteita / ei valittua
  if (!monthlyFeatured) return null;

  const goProfile = (slug) => {
    const d = localStorage.getItem("bilebox:selectedDate") || new Date().toISOString().split("T")[0];
    window.scrollTo({ top: 0, behavior: "smooth" });
    navigate(`/esiintyja/${slug}?date=${d}`);
  };

  return (
    <section className={`mt-8 rounded-2xl ${cardBg} backdrop-blur-md p-6`} data-aos="fade-up">
      <div className="mx-auto max-w-7xl">
        <h2 className={`font-customFont2 text-3xl text-black/80 dark:text-white/80 sm:text-4xl lg:text-5xl ${titleText} drop-shadow text-center`}>
          KUUKAUDEN ESIINTYJÄ
        </h2>
        <p className={`mt-2 text-lg xxxsm:text-xs xxsm:text-xs s24:text-xs xsm:text-xs sm:text-sm md:text-sm mdd:text-sm ${bodyText} text-center`}>
          Yleisön suosikit ja kovan palautteen kerääjä!
        </p>
      </div>

      <div className="mt-6 grid place-items-center">
        <div className="w-full max-w-3xl">
          <ArtistCard
            band={{
              ...monthlyFeatured,
              id: Number(monthlyFeatured.id),
              average_rating: Number(monthlyFeatured.average_rating ?? 0),
              total_reviews: Number(monthlyFeatured.total_reviews ?? monthlyFeatured.reviews_count ?? 0),
            }}
            onViewProfile={goProfile}
            highlight
          />
        </div>
      </div>
    </section>
  );
}
